import { EventTypesEnum, PerformanceNudgeEnum } from "@/enums/selfPractice";

export const PausedSessionExpiryHours = 48;

export const PracticeSpecificEvents: ReadonlyArray<string> = Object.freeze([
  EventTypesEnum.START_PRACTICE,
  EventTypesEnum.PAUSE_PRACTICE_POPUP_OPEN,
  EventTypesEnum.PAUSE_PRACTICE_POPUP_CLOSE,
  EventTypesEnum.USER_PAUSE_PRACTICE,
  EventTypesEnum.END_PRACTICE_POPUP_OPEN,
  EventTypesEnum.END_PRACTICE_POPUP_CLOSE,
  EventTypesEnum.AUTO_END_PRACTICE,
  EventTypesEnum.USER_END_PRACTICE,
  EventTypesEnum.END_PRACTICE_SUCCESSFUL,
]);

export const QuestionSpecificEvents: ReadonlyArray<string> = Object.freeze([
  EventTypesEnum.VIEW_QUESTION,
  EventTypesEnum.SKIP_QUESTION,
  EventTypesEnum.SELECT_ANSWER,
  EventTypesEnum.INPUT_ANSWER,
  EventTypesEnum.UPLOAD_ANSWER,
  EventTypesEnum.CLEAR_ANSWER,
  EventTypesEnum.SUBMIT_ANSWER,
  EventTypesEnum.VIEW_KSC,
  EventTypesEnum.VIEW_SOLUTION,
  EventTypesEnum.MARKED_FOR_CLARIFICATION,
  EventTypesEnum.MARKED_FOR_REVISION,
  EventTypesEnum.SUBMIT_REPORT_ERROR,
]);

export const AnswerSpecificEvents: ReadonlyArray<string> = Object.freeze([
  EventTypesEnum.SELECT_ANSWER,
  EventTypesEnum.INPUT_ANSWER,
  EventTypesEnum.CLEAR_ANSWER,
  EventTypesEnum.SUBMIT_ANSWER,
]);

export const EvaluationSpecificEvents: ReadonlyArray<string> = Object.freeze([
  EventTypesEnum.SKIP_QUESTION,
  EventTypesEnum.SUBMIT_ANSWER,
]);

export const GapEvents: ReadonlyArray<string> = Object.freeze([EventTypesEnum.RESUME_PRACTICE]);

export const QuestionMetrics = Object.freeze({
  AvgTime: "1 min",
  Accuracy: "50%",
});

export const PerformanceNudgeMessages = Object.freeze({
  [PerformanceNudgeEnum.WINNING_STREAK_10]: {
    PerformanceNudgeId: PerformanceNudgeEnum.WINNING_STREAK_10,
    MessageIcon: "winning-streak",
    MessageTitle: "Winning Streak",
    MessageText: "Awesome! You are on a hot streak of 10 correct questions.",
    MessageType: "success",
    ShowConfetti: true,
  },
  [PerformanceNudgeEnum.WINNING_STREAK_5]: {
    PerformanceNudgeId: PerformanceNudgeEnum.WINNING_STREAK_5,
    MessageIcon: "winning-streak",
    MessageTitle: "Winning Streak",
    MessageText: "Looks like you are on a winning streak! You got the last 5 questions correct.",
    MessageType: "success",
    ShowConfetti: true,
  },
  [PerformanceNudgeEnum.LOSING_STREAK_10]: {
    PerformanceNudgeId: PerformanceNudgeEnum.LOSING_STREAK_10,
    MessageIcon: "losing-streak",
    MessageTitle: "Losing Streak",
    MessageText: "Need a break? You just answered 10 questions incorrectly.",
    MessageType: "danger",
    ShowConfetti: false,
  },
  [PerformanceNudgeEnum.LOSING_STREAK_5]: {
    PerformanceNudgeId: PerformanceNudgeEnum.LOSING_STREAK_5,
    MessageIcon: "losing-streak",
    MessageTitle: "Losing Streak",
    MessageText: "Want to review the concepts? You got the last 5 questions wrong.",
    MessageType: "danger",
    ShowConfetti: false,
  },
  [PerformanceNudgeEnum.SKIPPED_PERCENT_50]: {
    PerformanceNudgeId: PerformanceNudgeEnum.SKIPPED_PERCENT_50,
    MessageIcon: "skipped-high",
    MessageTitle: "Skipped More Than 50%",
    MessageText:
      "You have skipped more than half the questions. Learn the concepts before starting practice.",
    MessageType: "danger",
    ShowConfetti: false,
    IsSticky: true,
  },
  [PerformanceNudgeEnum.SKIPPED_PERCENT_25]: {
    PerformanceNudgeId: PerformanceNudgeEnum.SKIPPED_PERCENT_25,
    MessageIcon: "skipped-high",
    MessageTitle: "Too Many Skipped",
    MessageText:
      "You have skipped quite a few questions. If they are not clear, you can mark them for clarification.",
    MessageType: "danger",
    ShowConfetti: false,
    IsSticky: true,
  },
  [PerformanceNudgeEnum.CONCEPT_MASTERY_10_HARD]: {
    PerformanceNudgeId: PerformanceNudgeEnum.CONCEPT_MASTERY_10_HARD,
    MessageIcon: "concept-mastery",
    MessageTitle: "Concept Aced",
    MessageText:
      "You have aced this concept. You just cracked 10 hard questions without breaking a sweat.",
    MessageType: "success",
    ShowConfetti: true,
  },
  [PerformanceNudgeEnum.CONCEPT_MASTERY_5_HARD]: {
    PerformanceNudgeId: PerformanceNudgeEnum.CONCEPT_MASTERY_5_HARD,
    MessageIcon: "concept-mastery",
    MessageTitle: "Concept Mastered",
    MessageText: "Kudos to you! You just answered 5 tough questions in this concept. Keep it up.",
    MessageType: "success",
    ShowConfetti: true,
  },
  [PerformanceNudgeEnum.CONCEPT_ACCURACY_HIGH]: {
    PerformanceNudgeId: PerformanceNudgeEnum.CONCEPT_ACCURACY_HIGH,
    MessageIcon: "concept-mastery",
    MessageTitle: "Concept Understood",
    MessageText: "Amazing!! You are doing well in this concept.",
    MessageType: "success",
    ShowConfetti: false,
  },
  [PerformanceNudgeEnum.CONCEPT_ACCURACY_LOW]: {
    PerformanceNudgeId: PerformanceNudgeEnum.CONCEPT_ACCURACY_LOW,
    MessageIcon: "concept-weak",
    MessageTitle: "Weak Concept",
    MessageText:
      "You made quite a few mistakes in this concept. Visit the Learn section to review.",
    MessageType: "danger",
    ShowConfetti: false,
  },
  [PerformanceNudgeEnum.QUESTION_TOUGH_NUT_CRACKED]: {
    PerformanceNudgeId: PerformanceNudgeEnum.QUESTION_TOUGH_NUT_CRACKED,
    MessageIcon: "tough-nut",
    MessageTitle: "Tough Nut Cracked",
    MessageText: "Hey... congrats! You just cracked a tough nut.",
    MessageType: "success",
    ShowConfetti: true,
  },
  [PerformanceNudgeEnum.QUESTION_EASY_MISSED]: {
    PerformanceNudgeId: PerformanceNudgeEnum.QUESTION_EASY_MISSED,
    MessageIcon: "easy-missed",
    MessageTitle: "Easy Question Missed",
    MessageText: "Oh! Looks like you missed an easy question - why don't you try that again?",
    MessageType: "danger",
    ShowConfetti: false,
    IsSticky: true,
  },
  [PerformanceNudgeEnum.QUESTION_CORRECT_AFTER_CONCEPT_VIEW]: {
    PerformanceNudgeId: PerformanceNudgeEnum.QUESTION_CORRECT_AFTER_CONCEPT_VIEW,
    MessageIcon: "concept-review",
    MessageTitle: "Concept Reviewed",
    MessageText: "Reviewing the concept helped. Try solving a few similar questions.",
    MessageType: "success",
    ShowConfetti: false,
  },
  [PerformanceNudgeEnum.EARN_MORE_SPEED_COINS]: {
    PerformanceNudgeId: PerformanceNudgeEnum.EARN_MORE_SPEED_COINS,
    MessageIcon: "speed-coins",
    MessageTitle: "More SpeEdCoins Coming",
    MessageText:
      "Nicely done! Keep up the accuracy and you will earn more SpeEdCoins for this session.",
    MessageType: "success",
    ShowConfetti: true,
    IsSticky: true,
  },
  [PerformanceNudgeEnum.EARN_SPEED_COINS]: {
    PerformanceNudgeId: PerformanceNudgeEnum.EARN_SPEED_COINS,
    MessageIcon: "speed-coins",
    MessageTitle: "Earn SpeEdCoins",
    MessageText: "Nice work... you are on track to earn some SpeEdCoins if you keep going.",
    MessageType: "success",
    ShowConfetti: false,
    IsSticky: true,
  },
  [PerformanceNudgeEnum.CORRECTED_COUNT_4X]: {
    PerformanceNudgeId: PerformanceNudgeEnum.CORRECTED_COUNT_4X,
    MessageIcon: "corrected-count",
    MessageTitle: "Become An Expert",
    MessageText: "Wow, amazing work! You will soon become an expert in these concepts.",
    MessageType: "success",
    ShowConfetti: true,
  },
  [PerformanceNudgeEnum.CORRECTED_COUNT_2X]: {
    PerformanceNudgeId: PerformanceNudgeEnum.CORRECTED_COUNT_2X,
    MessageIcon: "corrected-count",
    MessageTitle: "On The Right Track",
    MessageText: "Great... keep going!  You are on the right track - keep up the hard work.",
    MessageType: "success",
    ShowConfetti: true,
  },
  [PerformanceNudgeEnum.CORRECTED_COUNT_X]: {
    PerformanceNudgeId: PerformanceNudgeEnum.CORRECTED_COUNT_X,
    MessageIcon: "corrected-count",
    MessageTitle: "Good Job",
    MessageText:
      "Good job! You have solved more questions that the average student in this chapter.",
    MessageType: "success",
    ShowConfetti: false,
  },
  [PerformanceNudgeEnum.ACCURACY_VERY_LOW_SPEED_VERY_HIGH]: {
    PerformanceNudgeId: PerformanceNudgeEnum.ACCURACY_VERY_LOW_SPEED_VERY_HIGH,
    MessageIcon: "random-guess",
    MessageTitle: "Don't Guess Randomly",
    MessageText: "It seems you are guessing the answers instead of trying to solve the questions.",
    MessageType: "danger",
    ShowConfetti: false,
    IsSticky: true,
  },
  [PerformanceNudgeEnum.ACCURACY_HIGH_SPEED_HIGH]: {
    PerformanceNudgeId: PerformanceNudgeEnum.ACCURACY_HIGH_SPEED_HIGH,
    MessageIcon: "high-accuracy-high-speed",
    MessageTitle: "Keep It Up",
    MessageText: "Great job! You seem to be doing quite well... keep it up!",
    MessageType: "success",
    ShowConfetti: false,
    IsSticky: true,
  },
  [PerformanceNudgeEnum.ACCURACY_HIGH_SPEED_LOW]: {
    PerformanceNudgeId: PerformanceNudgeEnum.ACCURACY_HIGH_SPEED_LOW,
    MessageIcon: "high-accuracy-low-speed",
    MessageTitle: "Try To Speed Up",
    MessageText:
      "Good work! Your accuracy is good but you can try to solve questions a little faster.",
    MessageType: "success",
    ShowConfetti: false,
    IsSticky: true,
  },
  [PerformanceNudgeEnum.ACCURACY_LOW_SPEED_HIGH]: {
    PerformanceNudgeId: PerformanceNudgeEnum.ACCURACY_LOW_SPEED_HIGH,
    MessageIcon: "low-accuracy-high-speed",
    MessageTitle: "Slow Down & Focus",
    MessageText:
      "Looks like you are trying to go too fast. Focus on getting the right answer instead.",
    MessageType: "danger",
    ShowConfetti: false,
    IsSticky: true,
  },
  [PerformanceNudgeEnum.ACCURACY_LOW_SPEED_LOW]: {
    PerformanceNudgeId: PerformanceNudgeEnum.ACCURACY_LOW_SPEED_LOW,
    MessageIcon: "low-accuracy-low-speed",
    MessageTitle: "Review The Concepts",
    MessageText:
      "You need to improve both your accuracy and speed. Review concepts and videos first.",
    MessageType: "danger",
    ShowConfetti: false,
    IsSticky: true,
  },
});

export const AccuracyGroupThreshold = 0.1;
export const SpeedGroupThreshold = 0.2;
export const SpeedGroupThresholdHigh = 0.5;
export const ConceptAccuracyHighThreshold = 0.8;
export const ConceptAccuracyLowThreshold = 0.5;
export const SkippedPercentThreshold1 = 0.25;
export const SkippedPercentThreshold2 = 0.5;
export const CorrectedMultipleThreshold1 = 1;
export const CorrectedMultipleThreshold2 = 2;
export const CorrectedMultipleThreshold3 = 4;
export const SpeedCoinsAccuracyThreshold1 = 0.5;
export const SpeedCoinsAttemptedThreshold1 = 20;
export const SpeedCoinsAccuracyThreshold2 = 0.8;
export const SpeedCoinsAttemptedThreshold2 = 30;
export const RelativeAccuracyThreshold1 = 0.1;
export const RelativeAccuracyThreshold2 = 0.2;
export const RelativeSpeedThreshold1 = 1.2;
export const RelativeSpeedThreshold2 = 2;
export const MaxPerformanceNudges = 2;
export const CorrectedMedianMinimum = 20;
export const GlobalAccuracyMinimum = 0.5;
export const GlobalAvgTimeMinimum = 30;
